/**
 * record_times rows for display and entry: how a time reads in a cell, its
 * kind, and parsing what someone types ("nov 1", "fri 2-3pm", "apr 1-4").
 *
 * A row stores UTC instants plus the zone it was entered in. Everything here
 * reads a row in that zone (a 2pm Chicago meeting reads "2pm CT"), and names
 * the zone only when it isn't the viewer's.
 */
import { getBrowserTimeZone, getDateStringInZone, getTimeStringInZone, parseInZone } from "./date"

/** A record_times row, as read (snake_case) — the columns display needs. */
export interface TimeRow {
  id?: number | string
  time_type?: string | null
  title?: string | null
  start_at?: string | null
  end_at?: string | null
  timezone?: string | null
  all_day?: boolean | null
  rrule?: string | null
  capacity?: number | null
}

export type TimeKind = "instant" | "event" | "hours" | "slot" | "lifetime"

/** The five kinds, in picker order, with the Font Awesome icon each shows where
 *  kinds mix (the aggregate Times field). */
export const TIME_KINDS: ReadonlyArray<{
  key: TimeKind
  label: string
  icon: string
  note: string
}> = [
  {
    key: "instant",
    label: "Instant",
    icon: "clock",
    note: "One moment: an email, a note, a deadline.",
  },
  {
    key: "event",
    label: "Event",
    icon: "calendar-day",
    note: "Starts and ends: a meeting, a conference.",
  },
  {
    key: "hours",
    label: "Hours",
    icon: "repeat",
    note: "Repeats every week. Pick the days and the window.",
  },
  { key: "slot", label: "Slot", icon: "ticket", note: "A bookable window with a seat count." },
  {
    key: "lifetime",
    label: "Lifetime",
    icon: "hourglass-half",
    note: "How long it exists or runs. Ongoing has no end yet.",
  },
]

const EVENT_KIND = TIME_KINDS[1]!
export const timeKindOf = (row: TimeRow) =>
  TIME_KINDS.find((k) => k.key === row.time_type) ?? EVENT_KIND

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const BYDAY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"]

// DATES (plain "YYYY-MM-DD" calendar days) ======================================

const ymd = (date: string) => {
  const [y = 0, m = 1, d = 1] = date.split("-").map(Number)
  return { y, m, d }
}
const monthName = (m: number) => MONTHS[m - 1] ?? ""
const utcDay = (date: string) => {
  const { y, m, d } = ymd(date)
  return Date.UTC(y, m - 1, d)
}
export const isoDay = (y: number, m: number, d: number) =>
  new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10)
export const addDays = (date: string, n: number) => {
  const { y, m, d } = ymd(date)
  return isoDay(y, m, d + n)
}
export const addMonths = (date: string, n: number) => {
  const { y, m, d } = ymd(date)
  return isoDay(y, m + n, d)
}
export const daysBetween = (a: string, b: string) => Math.round((utcDay(b) - utcDay(a)) / 864e5)
export const weekdayOf = (date: string) => new Date(utcDay(date)).getUTCDay()

/** Today, as a calendar day in the viewer's zone. */
export const todayDay = (now: Date = new Date()) => getDateStringInZone(now, getBrowserTimeZone())

// READING A ROW ================================================================

/** A row's start and end as wall-clock parts in its own zone. */
export interface TimeParts {
  kind: TimeKind
  date: string
  time: string | null
  endDate: string | null
  endTime: string | null
  allDay: boolean
  tz: string
}

export function timeParts(row: TimeRow): TimeParts | null {
  if (!row.start_at) return null
  const tz = row.timezone || getBrowserTimeZone()
  const allDay = !!row.all_day
  const end = row.end_at ?? null
  return {
    kind: timeKindOf(row).key,
    date: getDateStringInZone(row.start_at, tz),
    time: allDay ? null : getTimeStringInZone(row.start_at, tz),
    endDate: end ? getDateStringInZone(end, tz) : null,
    endTime: end && !allDay ? getTimeStringInZone(end, tz) : null,
    allDay,
    tz,
  }
}

const clock = (time: string, withSuffix = true) => {
  const [h24 = 0, min = 0] = time.split(":").map(Number)
  const h = h24 % 12 || 12
  const text = min ? `${h}:${String(min).padStart(2, "0")}` : String(h)
  return withSuffix ? `${text}${h24 >= 12 ? "pm" : "am"}` : text
}
const meridiem = (time: string) => (Number(time.split(":")[0]) >= 12 ? "pm" : "am")
const clockRange = (a: string, b: string) =>
  meridiem(a) === meridiem(b) ? `${clock(a, false)}–${clock(b)}` : `${clock(a)}–${clock(b)}`

const monthDay = (date: string) => {
  const { m, d } = ymd(date)
  return `${monthName(m)} ${d}`
}
const yearSuffix = (date: string, thisYear: number) => {
  const { y } = ymd(date)
  return y === thisYear ? "" : `, ${y}`
}

/** The weekdays of a weekly rrule, read as people say them: "Mon–Fri". */
export function describeWeekdays(rrule: string | null | undefined): string {
  const match = rrule?.match(/BYDAY=([A-Z,]+)/)
  const days = match?.[1]?.split(",") ?? []
  const key = days.join(",")
  if (key === "MO,TU,WE,TH,FR") return "Mon–Fri"
  if (key === "SA,SU" || key === "SU,SA") return "Weekends"
  if (days.length === 7) return "Every day"
  return (
    days
      .map((d) => WEEKDAYS[BYDAY.indexOf(d)])
      .filter(Boolean)
      .join(", ") || "Weekly"
  )
}

export const weeklyRrule = (days: string[]) =>
  `FREQ=WEEKLY;BYDAY=${BYDAY.filter((d) => days.includes(d)).join(",")}`
export const rruleWeekdays = (rrule: string | null | undefined) =>
  rrule?.match(/BYDAY=([A-Z,]+)/)?.[1]?.split(",") ?? []
export const WEEKDAY_CODES = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const
export const weekdayLabel = (code: string) => WEEKDAYS[BYDAY.indexOf(code)] ?? code

/** How one row reads in a cell: the line itself, the zone when it isn't the
 *  viewer's, and the detail line a tall row shows under it. */
export interface TimeDisplay {
  text: string
  zone: string
  detail: string
}

function lengthOf(row: TimeRow, p: TimeParts, today: string): string {
  if (p.kind === "instant" || p.kind === "hours") return ""
  if (p.kind === "lifetime") {
    const years = (end: string) => Math.round((daysBetween(p.date, end) / 365) * 10) / 10
    return p.endDate ? `${years(p.endDate)} years` : `${years(today)} years so far`
  }
  if (!p.endDate) return ""
  if (p.allDay) {
    const days = daysBetween(p.date, p.endDate) + 1
    return days === 1 ? "All day" : `${days} days`
  }
  const minutes = Math.round((Date.parse(row.end_at!) - Date.parse(row.start_at!)) / 6e4)
  if (minutes <= 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h >= 24) return `${Math.round(h / 24)} days`
  return [h ? `${h}h` : "", m ? `${m}m` : ""].filter(Boolean).join(" ")
}

/** "in 3 months", "2 weeks ago", "tomorrow". */
export function relativeDay(date: string, today: string = todayDay()): string {
  const n = daysBetween(today, date)
  if (n === 0) return "today"
  if (n === 1) return "tomorrow"
  if (n === -1) return "yesterday"
  const a = Math.abs(n)
  const unit =
    a < 14
      ? `${a} days`
      : a < 60
        ? `${Math.round(a / 7)} weeks`
        : a < 365
          ? `${Math.round(a / 30.4)} months`
          : `${Math.round(a / 365)} years`
  return n > 0 ? `in ${unit}` : `${unit} ago`
}

/**
 * How `row` reads in a cell. `single` is a field holding one time (a deadline):
 * its detail is the weekday and distance. A row among many details its length,
 * seats and title instead.
 */
export function formatTime(
  row: TimeRow,
  opts: { single?: boolean; now?: Date; viewerZone?: string } = {}
): TimeDisplay {
  const p = timeParts(row)
  if (!p) return { text: row.title ?? "", zone: "", detail: "" }
  const today = todayDay(opts.now)
  const thisYear = ymd(today).y
  const viewerZone = opts.viewerZone ?? getBrowserTimeZone()
  const timed = !p.allDay && p.kind !== "lifetime"
  const zone = timed && p.tz !== viewerZone ? zoneAbbreviation(p.tz) : ""

  let text: string
  if (p.kind === "hours") {
    text = `${describeWeekdays(row.rrule)}${p.time && p.endTime ? ` ${clockRange(p.time, p.endTime)}` : ""}`
  } else if (p.kind === "lifetime" && !p.endDate) {
    const { y, m } = ymd(p.date)
    text = `Since ${monthName(m)} ${y}`
  } else {
    const end = p.kind === "instant" ? p.date : (p.endDate ?? p.date)
    const span = daysBetween(p.date, end)
    if (span === 0) {
      text = monthDay(p.date) + yearSuffix(p.date, thisYear)
      if (timed && p.time) {
        const ranged = p.kind !== "instant" && p.endTime && p.endTime !== p.time
        text += `, ${ranged ? clockRange(p.time, p.endTime!) : clock(p.time)}`
      }
    } else if (span > 90) {
      const s = ymd(p.date)
      const e = ymd(end)
      text = `${monthName(s.m)} ${s.y} – ${monthName(e.m)} ${e.y}`
    } else if (!timed) {
      const s = ymd(p.date)
      const e = ymd(end)
      text =
        (s.m === e.m && s.y === e.y
          ? `${monthDay(p.date)}–${e.d}`
          : `${monthDay(p.date)} – ${monthDay(end)}`) + yearSuffix(end, thisYear)
    } else {
      text = `${monthDay(p.date)} ${clock(p.time!)} – ${monthDay(end)} ${clock(p.endTime!)}${yearSuffix(end, thisYear)}`
    }
  }

  let detail: string
  if (opts.single) {
    detail = `${WEEKDAYS[weekdayOf(p.date)]} · ${relativeDay(p.date, today)}`
  } else {
    const parts = [lengthOf(row, p, today)]
    if (p.kind === "slot" && row.capacity != null) parts.push(`${row.capacity} seats`)
    if (row.title) parts.push(row.title)
    detail = parts.filter(Boolean).join(" · ")
  }
  return { text, zone, detail }
}

/** The long form of a single day: "Sun, Nov 1, 2026" (+ " at 5pm"). */
export function longDay(date: string, time?: string | null): string {
  const { y } = ymd(date)
  return `${WEEKDAYS[weekdayOf(date)]}, ${monthDay(date)}, ${y}${time ? ` at ${clock(time)}` : ""}`
}

/** The short form of a single day as a cell shows it: "Nov 1", "Jan 8, 2027". */
export function shortDay(date: string, time?: string | null, today: string = todayDay()): string {
  return `${monthDay(date)}${yearSuffix(date, ymd(today).y)}${time ? `, ${clock(time)}` : ""}`
}

export function zoneAbbreviation(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date())
    const name = parts.find((p) => p.type === "timeZoneName")?.value ?? tz
    // "PST"/"PDT" → "PT": the season is noise in a cell.
    return /^[A-Z]{1,2}[SD]T$/.test(name) ? `${name[0]}${name.length === 4 ? name[1] : ""}T` : name
  } catch {
    return tz
  }
}

/** Latest start first; a time with no start last. */
export function latestFirst<T extends TimeRow>(rows: T[]): T[] {
  const startOf = (r: TimeRow) => Date.parse(r.start_at ?? "") || -Infinity
  return [...rows].sort((a, b) => startOf(b) - startOf(a))
}

// TYPING A TIME ================================================================

const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
]
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

/** What a typed time means: a day, optionally a time or a time range, or a
 *  span of days. */
export interface ParsedWhen {
  date: string
  endDate?: string
  time?: string
  endTime?: string
  allDay: boolean
}
export type ParseResult = ParsedWhen | { empty: true } | { error: string }

export const isParsed = (r: ParseResult): r is ParsedWhen => "date" in r

function hhmm(h: string | undefined, min: string | undefined, suffix: string | undefined): string {
  let hour = Number(h ?? 0)
  const m = Number(min ?? 0)
  // No am/pm: a working-day guess — 1–7 is the afternoon, 8–11 the morning.
  const ap = suffix?.[0] ?? ((hour >= 1 && hour <= 7) || hour === 12 ? "p" : "a")
  if (ap === "p" && hour < 12) hour += 12
  if (ap === "a" && hour === 12) hour = 0
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

/**
 * Read a typed time. A month and day with no year means the next one: "jan 8"
 * typed in October is next January — or, editing an existing time (`base`), the
 * one nearest it. `base` is the day a bare time ("3pm") or a
 * nudge ("+2w") is relative to. `ranges: false` refuses spans, for a field that
 * holds one moment.
 */
export function parseWhen(
  input: string,
  opts: { base?: string | null; today?: string; ranges?: boolean } = {}
): ParseResult {
  const t = input.toLowerCase().replace(/\s+/g, " ").trim()
  if (!t) return { empty: true }
  const today = opts.today ?? todayDay()
  const base = opts.base ?? null
  const ranges = opts.ranges ?? true
  // A month and day with no year: editing an existing time, the one nearest it
  // ("jan 27" on a Jan 26 row stays that January); otherwise the next one.
  const inferYear = (date: string, explicitYear: boolean) => {
    if (explicitYear) return date
    if (base) {
      const candidates = [-12, 0, 12].map((n) =>
        addMonths(date, n + (ymd(base).y - ymd(date).y) * 12)
      )
      return candidates.reduce((a, b) =>
        Math.abs(daysBetween(base, b)) < Math.abs(daysBetween(base, a)) ? b : a
      )
    }
    return daysBetween(today, date) < -30 ? addMonths(date, 12) : date
  }

  let date: string | null = null
  let m: RegExpMatchArray | null
  if ((m = t.match(/^(today|tod)\b/))) date = today
  else if ((m = t.match(/^(tomorrow|tmrw?|tom)\b/))) date = addDays(today, 1)
  else if ((m = t.match(/^yesterday\b/))) date = addDays(today, -1)
  else if ((m = t.match(/^([+-])(\d+) ?(d|w|m)[a-z]*/))) {
    const n = Number(m[2]) * (m[1] === "-" ? -1 : 1)
    const from = base ?? today
    date =
      m[3] === "d" ? addDays(from, n) : m[3] === "w" ? addDays(from, 7 * n) : addMonths(from, n)
  } else if ((m = t.match(/^(next )?(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/))) {
    let diff = (DAY_KEYS.indexOf(m[2] ?? "") - weekdayOf(today) + 7) % 7 || 7
    if (m[1]) diff += 7
    date = addDays(today, diff)
  } else if ((m = t.match(/^([a-z]{3})[a-z]*\.? (\d{1,2})(?:st|nd|rd|th)?(?:,? (\d{4}))?/))) {
    const month = MONTH_KEYS.indexOf(m[1] ?? "")
    if (month < 0) return { error: `Can’t read “${input.trim()}”` }
    date = inferYear(isoDay(m[3] ? Number(m[3]) : ymd(today).y, month + 1, Number(m[2])), !!m[3])
  } else if ((m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/))) {
    date = isoDay(Number(m[1]), Number(m[2]), Number(m[3]))
  } else if ((m = t.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/))) {
    const year = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : ymd(today).y
    date = inferYear(isoDay(year, Number(m[1]), Number(m[2])), !!m[3])
  }

  let rest = date && m ? t.slice(m[0].length).trim() : t
  if (!date) {
    if (!base) return { error: "Start with a date: nov 1, 11/1, fri…" }
    date = base
  }
  rest = rest.replace(/^(,|at|@) ?/, "")
  const oneMoment = { error: "This field holds one moment, not a range" }

  const days = rest.match(
    /^(?:-|–|to|thru|through) ?(?:([a-z]{3})[a-z]* )?(\d{1,2})(?:,? (\d{4}))?$/
  )
  if (days) {
    if (!ranges) return oneMoment
    const start = ymd(date)
    const month = days[1] ? MONTH_KEYS.indexOf(days[1]) + 1 : start.m
    if (month < 1) return { error: `Can’t read “${rest}”` }
    const year = days[3] ? Number(days[3]) : start.y
    let endDate = isoDay(year, month, Number(days[2]))
    if (endDate < date) endDate = isoDay(year + 1, month, Number(days[2]))
    return { date, endDate, allDay: true }
  }
  if (!rest) return { date, allDay: true }
  if (rest === "noon") return { date, time: "12:00", allDay: false }

  const range = rest.match(
    /^(\d{1,2})(?::(\d{2}))? ?(am|pm|a|p)? ?(?:-|–|to) ?(\d{1,2})(?::(\d{2}))? ?(am|pm|a|p)?$/
  )
  if (range) {
    if (!ranges) return oneMoment
    const endSuffix = range[6]
    const endTime = hhmm(range[4], range[5], endSuffix)
    let time = hhmm(range[1], range[2], range[3] ?? endSuffix)
    if (time > endTime && !range[3]) time = hhmm(range[1], range[2], "a")
    return { date, endDate: date, time, endTime, allDay: false }
  }
  const single = rest.match(/^(\d{1,2})(?::(\d{2}))? ?(am|pm|a|p)?$/)
  if (single) return { date, time: hhmm(single[1], single[2], single[3]), allDay: false }
  return { error: `Can’t read “${rest}”` }
}

/** The text that parses back to `p` — what an edit box shows for a row. */
export function typedForm(p: TimeParts): string {
  const { y, m, d } = ymd(p.date)
  const thisYear = ymd(todayDay()).y
  const day = `${MONTH_KEYS[m - 1]} ${d}${y === thisYear ? "" : ` ${y}`}`
  if (p.kind === "instant") return p.allDay || !p.time ? day : `${day} ${clock(p.time)}`
  if (p.allDay) {
    if (!p.endDate || p.endDate === p.date) return day
    const e = ymd(p.endDate)
    return `${day} - ${e.m !== m ? `${MONTH_KEYS[e.m - 1]} ` : ""}${e.d}`
  }
  if (!p.time) return day
  if (p.endDate && p.endDate !== p.date && p.endTime) return day
  if (!p.endTime || p.endTime === p.time) return `${day} ${clock(p.time)}`
  return `${day} ${clockRange(p.time, p.endTime).replace("–", "-")}`
}

// WRITING A ROW ================================================================

/** The stored columns for wall-clock parts in `tz`: UTC instants plus all_day.
 *  An all-day day starts at midnight in the zone; a run of all-day days ends
 *  at 23:59 on its last day; a single all-day day and an instant have no end. */
export function storedInstants(
  p: {
    kind: TimeKind
    date: string
    time?: string | null
    endDate?: string | null
    endTime?: string | null
    allDay: boolean
  },
  tz: string
): { start_at: string; end_at: string | null; all_day: boolean } {
  const allDay = p.allDay || p.kind === "lifetime"
  const start_at = parseInZone(p.date, allDay ? "00:00" : (p.time ?? "00:00"), tz)
  let end_at: string | null = null
  if (p.kind !== "instant") {
    const endDate = p.endDate ?? p.date
    if (allDay) {
      if (p.endDate && (p.kind === "lifetime" || endDate !== p.date))
        end_at = parseInZone(endDate, "23:59", tz)
    } else if (p.endTime) {
      end_at = parseInZone(endDate, p.endTime, tz)
    }
  }
  return { start_at, end_at, all_day: allDay }
}
